import React, { useState } from 'react';
import { ChevronDown, Facebook, Twitter, Instagram, MapPin, Phone, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Footer = () => {
  const [openSection, setOpenSection] = useState(null);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const footerSections = {
    empresa: {
      title: 'Nuestra Empresa',
      links: [
        'Envíos y Devoluciones',
        'Aviso legal / Condiciones de uso',
        'Política de privacidad',
        'Formas de pago',
        'Política de cookies',
        'Sobre nosotros',
        'Horario',
        'Contacte con nosotros',
        'Mapa del sitio',
        'Tiendas'
      ]
    },
    categorias: {
      title: 'Categorías',
      links: [
        'Americano y Europeo',
        'Cómo dibujar',
        'Juegos',
        'Libros de ilustraciones - Artbooks',
        'Libros técnicos',
        'Libros y novelas',
        'Manga',
        'Merchandising'
      ]
    },
    cuenta: {
      title: 'Su Cuenta',
      links: [
        'Información personal',
        'Pedidos',
        'Facturas por abono',
        'Direcciones',
        'Mis alertas'
      ]
    }
  };

  return (
    <footer className="bg-muted/30 border-t border-border">
      <div className="container py-12">
        {/* Desktop Layout */}
        <div className="hidden md:grid md:grid-cols-4 gap-8 mb-12">
          {/* Company Section */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{footerSections.empresa.title}</h3>
            <ul className="space-y-2">
              {footerSections.empresa.links.map((link, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors relative inline-block group"
                  >
                    {link}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-primary transition-all group-hover:w-full" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories Section */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{footerSections.categorias.title}</h3>
            <ul className="space-y-2">
              {footerSections.categorias.links.map((link, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors relative inline-block group"
                  >
                    {link}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-primary transition-all group-hover:w-full" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Account Section */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{footerSections.cuenta.title}</h3>
            <ul className="space-y-2">
              {footerSections.cuenta.links.map((link, index) => (
                <li key={index}>
                  <a
                    href="#"
                    className="text-sm text-muted-foreground hover:text-primary transition-colors relative inline-block group"
                  >
                    {link}
                    <span className="absolute bottom-0 left-0 w-0 h-px bg-primary transition-all group-hover:w-full" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Section */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contacto</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p>C/ Jesús, 25 Bajo izquierda</p>
                  <p>46007 Valencia, España</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>963943335</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@laorda.com" className="hover:text-primary transition-colors">
                  info@laorda.com
                </a>
              </div>
              <div className="flex gap-3 pt-4">
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                  aria-label="Twitter"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Accordion Layout */}
        <div className="md:hidden space-y-4 mb-8">
          {Object.entries(footerSections).map(([key, section]) => (
            <div key={key} className="border-b border-border">
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between py-4 text-left font-semibold"
              >
                {section.title}
                <motion.div
                  animate={{ rotate: openSection === key ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>
              <AnimatePresence>
                {openSection === key && (
                  <motion.ul
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="pb-4 space-y-2 overflow-hidden"
                  >
                    {section.links.map((link, index) => (
                      <li key={index}>
                        <a
                          href="#"
                          className="text-sm text-muted-foreground hover:text-primary transition-colors block"
                        >
                          {link}
                        </a>
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          ))}

          {/* Mobile Contact Section */}
          <div className="py-4">
            <h3 className="font-semibold text-lg mb-4">Contacto</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <div>
                  <p>C/ Jesús, 25 Bajo izquierda</p>
                  <p>46007 Valencia, España</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>963943335</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@laorda.com" className="hover:text-primary transition-colors">
                  info@laorda.com
                </a>
              </div>
              <div className="flex gap-3 pt-4">
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                  aria-label="Twitter"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  className="w-8 h-8 rounded-full bg-muted hover:bg-primary hover:text-primary-foreground flex items-center justify-center transition-all"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-muted/50 border-t border-border">
        <div className="container py-6 text-center">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} La Orda. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;